import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import {
  DocumentArrowDownIcon,
  EyeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const SubscriptionHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [, setPreviewImageError] = useState(false);

  useEffect(() => {
    loadSubscriptionHistory();
  }, []);

  const loadSubscriptionHistory = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/auth/subscription-history');
      const historyData = response.data.history || [];
      
      // Sort to show current subscription first
      const sorted = historyData.sort((a, b) => {
        if (a.is_current_subscription && !b.is_current_subscription) return -1;
        if (!a.is_current_subscription && b.is_current_subscription) return 1;
        return new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at);
      });
      
      setHistory(sorted);
    } catch (error) {
      console.error('Failed to load subscription history:', error);
      toast.error('Failed to load subscription history');
    } finally {
      setLoading(false);
    }
  };

  const viewRequestDetails = (request) => {
    setSelectedRequest(request);
    setPreviewImageError(false);
    setShowDetailsModal(true);
  };

  const downloadPaymentProof = (request) => {
    if (!request.payment_proof_url) {
      toast.error('No payment proof available');
      return;
    }
    const url = `/api/admin/payment-proof/${request.id}/${request.payment_proof_url}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = request.payment_proof_url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: {
        emoji: '⏳',
        label: 'Pending Payment',
        bg: 'bg-yellow-100',
        text: 'text-yellow-800'
      },
      paid: {
        emoji: '✅',
        label: 'Payment Received',
        bg: 'bg-blue-100',
        text: 'text-blue-800'
      },
      active: {
        emoji: '🟢',
        label: 'Active',
        bg: 'bg-green-100',
        text: 'text-green-800'
      },
      cancelled: {
        emoji: '❌',
        label: 'Cancelled',
        bg: 'bg-red-100',
        text: 'text-red-800'
      }
    };
    return badges[status] || badges.pending;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <ClockIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">No Subscription Found</h3>
        <p className="text-gray-600 mt-2">You don't have an active subscription yet. Please upgrade to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Subscription History</h2>

      <div className="grid gap-4">
        {history.map((request) => (
          <div
            key={request.id || 'current-subscription'}
            className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 border-l-4 ${
              request.is_current_subscription ? 'border-green-500' : 'border-blue-500'
            } ${request.is_current_subscription ? 'ring-2 ring-green-200' : ''}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status).bg} ${getStatusBadge(request.status).text}`}>
                    {request.is_current_subscription && request.status === 'active' && '⭐'}
                    {getStatusBadge(request.status).emoji} {getStatusBadge(request.status).label}
                  </span>
                  <span className="text-sm text-gray-500">
                    {request.is_current_subscription 
                      ? `Current${request.expires_at ? ` • Expires: ${new Date(request.expires_at).toLocaleDateString()}` : ''}`
                      : `Requested ${new Date(request.created_at || request.updated_at).toLocaleDateString()}`
                    }
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 mt-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Package</p>
                    <p className="font-semibold text-gray-900">{request.package}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Duration</p>
                    <p className="font-semibold text-gray-900">{request.months} months</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Total Amount</p>
                    <p className="font-semibold text-gray-900">PKR {request.total_price?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">{request.is_current_subscription ? 'Expires' : 'Status Date'}</p>
                    <p className="font-semibold text-gray-900">
                      {request.is_current_subscription && request.expires_at
                        ? new Date(request.expires_at).toLocaleDateString()
                        : new Date(request.updated_at || request.created_at).toLocaleDateString()
                      }
                    </p>
                  </div>
                </div>

                {request.admin_notes && (
                  <div className="mt-3 p-3 bg-gray-50 rounded border-l-2 border-gray-300">
                    <p className="text-xs text-gray-500 uppercase mb-1">Admin Notes</p>
                    <p className="text-sm text-gray-700">{request.admin_notes}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => viewRequestDetails(request)}
                className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                View
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-90vh overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Subscription Request Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-white hover:bg-blue-800 p-1 rounded"
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Request Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Package</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedRequest.package}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedRequest.status).bg} ${getStatusBadge(selectedRequest.status).text}`}>
                      {getStatusBadge(selectedRequest.status).emoji} {getStatusBadge(selectedRequest.status).label}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Duration</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedRequest.months} months</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Total Amount</p>
                  <p className="text-lg font-semibold text-green-600">PKR {selectedRequest.total_price?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Requested Date</p>
                  <p className="text-gray-900">{new Date(selectedRequest.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Last Updated</p>
                  <p className="text-gray-900">{new Date(selectedRequest.updated_at).toLocaleString()}</p>
                </div>
              </div>

              {/* Payment Proof */}
              {selectedRequest.payment_proof_url && (
                <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-blue-600" />
                    Payment Proof
                  </h4>
                  
                  {/* Image Preview */}
                  <div className="mb-4 bg-white p-2 rounded border border-blue-200">
                    <img
                      src={`/api/admin/payment-proof/${selectedRequest.id}/${selectedRequest.payment_proof_url}`}
                      alt="Payment Proof"
                      className="w-full h-auto max-h-80 object-contain rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        setPreviewImageError(true);
                      }}
                    />
                  </div>

                  {/* File Info & Download */}
                  <div className="flex items-center justify-between bg-white p-3 rounded border border-blue-200">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedRequest.payment_proof_url}</p>
                      <p className="text-xs text-gray-500 mt-1">Uploaded on {new Date(selectedRequest.created_at).toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => downloadPaymentProof(selectedRequest)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      Download
                    </button>
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              {selectedRequest.admin_notes && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Admin Notes</h4>
                  <p className="text-sm text-gray-600">{selectedRequest.admin_notes}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionHistory;